import _ from "lodash";
import { isIchOrganization } from "../constants/ichOrganizations";

/**
 * Cloud reports an embedded Price List can ask Cloud to print: the ICH Status/History "Print Invoice"
 * and "Print Portfolio" reports. Cloud renders them from the saved job with the Price List's current
 * invoice items applied, so the printout matches the screen. Every ICH user can print them, as on
 * Status/History.
 */
export const ICH_HOST_REPORTS = Object.freeze([
	Object.freeze({ id: "ich-selections-2", label: "Invoice" }),
	Object.freeze({ id: "ich-selections-portfolio-2", label: "Portfolio" })
]);

export function getHostReportsForOrg(orgId) {
	return isIchOrganization(orgId) ? ICH_HOST_REPORTS : [];
}

export function findHostReportForOrg(orgId, reportId) {
	const id = _.toString(reportId).trim();
	return getHostReportsForOrg(orgId).find(report => report.id === id) || null;
}

const isPlainObject = value => Boolean(value && _.isPlainObject(value));

/**
 * Validate and clone the transient pricing an embedded Price List sends with a report request. Only
 * the invoice items are taken: the ICH reports read the job's invoiceItems, and the activity,
 * customer, company, job and S3 identity remain owned by AmbiFi Cloud.
 *
 * @param {*} pricing Candidate request.pricing value.
 * @returns {Object|null} A detached { invoiceItems } draft, or null when invalid.
 */
export function normalizeHostReportDraft(pricing) {
	if (!isPlainObject(pricing)
		|| !Array.isArray(pricing.invoiceItems)
		|| !pricing.invoiceItems.every(isPlainObject)) {
		return null;
	}
	return { invoiceItems: _.cloneDeep(pricing.invoiceItems) };
}

/**
 * Overlay a validated draft onto a Cloud-owned activity.
 *
 * @param {Object} activity The saved job read from S3.
 * @param {Object} draft The Price List draft (see normalizeHostReportDraft).
 * @returns {Object} A detached activity with only invoiceItems replaced.
 */
export function applyHostReportDraft(activity = {}, draft = null) {
	const detached = _.cloneDeep(isPlainObject(activity) ? activity : {});
	const normalizedDraft = normalizeHostReportDraft(draft);
	return normalizedDraft ? { ...detached, invoiceItems: normalizedDraft.invoiceItems } : detached;
}
